import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ReferenceProvider } from "@/context/ReferenceContext";
import { PresentationProvider } from "@/context/PresentationContext";
import { UniversalIntakeProvider } from "@/context/UniversalIntakeContext";
import { RestrictedVaultProvider } from "@/context/RestrictedVaultContext";
import { CopilotProvider } from "@/context/CopilotContext";
import { GuidedTourProvider } from "@/context/GuidedTourContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AgentCommandCenter from "@/pages/AgentCommandCenter";
import AgencyCommandCenter from "@/pages/AgencyCommandCenter";
import SelfQuote from "@/pages/SelfQuote";
import LeadPipeline from "@/pages/LeadPipeline";
import WorkQueue from "@/pages/WorkQueue";
import IntakeQueue from "@/pages/IntakeQueue";
import Quotes from "@/pages/Quotes";
import Requote from "@/pages/Requote";
import Policies from "@/pages/Policies";
import FollowUps from "@/pages/FollowUps";
import Commissions from "@/pages/Commissions";
import Scorecard from "@/pages/Scorecard";
import Team from "@/pages/Team";
import AgencyAdmin from "@/pages/AgencyAdmin";
import Platform from "@/pages/Platform";
import Settings from "@/pages/Settings";
import Import from "@/pages/Import";
import BookRecovery from "@/pages/BookRecovery";
import CredentialVault from "@/pages/CredentialVault";
import CredentialMigrationReview from "@/pages/CredentialMigrationReview";
import LeadReview from "@/pages/LeadReview";
import VendorPortal from "@/pages/VendorPortal";
import Telephony from "@/pages/Telephony";
import SophiaOps from "@/pages/SophiaOps";
import Onboarding from "@/pages/Onboarding";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import GrowthHub from "@/pages/GrowthHub";
import CampaignDirector from "@/pages/CampaignDirector";
import GrowthCommandCenter from "@/pages/GrowthCommandCenter";
import CampaignLaunchpad from "@/pages/CampaignLaunchpad";
import ContentStudio from "@/pages/ContentStudio";
import MarketingOperations from "@/pages/MarketingOperations";
import ExclusiveAgencyLeads from "@/pages/ExclusiveAgencyLeads";
import LeadMarketplace from "@/pages/LeadMarketplace";
import BuyerMarketplace from "@/pages/BuyerMarketplace";
import MarketplaceLanding from "@/pages/MarketplaceLanding";
import CampaignLanding from "@/pages/CampaignLanding";
import { PrivacyPolicy, Terms, Contact } from "@/pages/LegalPages";
import { PublicHome, PublicFeatures, PublicPricing, PublicRequestDemo } from "@/pages/PublicSite";
import Appointments from "@/pages/Appointments";
import BookingPage from "@/pages/BookingPage";

function Protected({ children, allow }) {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role === "vendor" && (!allow || !allow.includes("vendor"))) return <Navigate to="/vendor" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user && user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "platform_owner") return <Navigate to="/growth-command" replace />;
  if (user?.role === "bpo_closer") return <Navigate to="/telephony" replace />;
  return user?.role === "agent" ? <AgentCommandCenter /> : <Dashboard />;
}

function RootRoute() {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;
  if (user === false) return <PublicHome />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role === "vendor") return <Navigate to="/vendor" replace />;
  return <Layout><HomeRoute /></Layout>;
}

function ChangePasswordRoute() {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <ChangePassword />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PresentationProvider>
          <ReferenceProvider>
          <UniversalIntakeProvider>
          <RestrictedVaultProvider>
          <CopilotProvider>
          <GuidedTourProvider>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/quote/:slug" element={<SelfQuote />} />
            <Route path="/c/:token" element={<CampaignLanding />} />
            <Route path="/final-expense/:token" element={<MarketplaceLanding />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/features" element={<PublicFeatures />} />
            <Route path="/pricing" element={<PublicPricing />} />
            <Route path="/request-demo" element={<PublicRequestDemo />} />
            <Route path="/book/:token" element={<BookingPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={<ChangePasswordRoute />} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/command-center" element={<Protected allow={["admin", "ops_manager"]}><AgencyCommandCenter /></Protected>} />
            <Route path="/my-work" element={<Protected allow={["admin", "ops_manager", "agent", "bpo_closer"]}><AgentCommandCenter /></Protected>} />
            <Route path="/pipeline" element={<Protected><LeadPipeline /></Protected>} />
            <Route path="/queue" element={<Protected allow={["admin", "ops_manager", "agent", "bpo", "bpo_closer"]}><WorkQueue /></Protected>} />
            <Route path="/intake" element={<Protected><IntakeQueue /></Protected>} />
            <Route path="/lead-review" element={<Protected allow={["admin", "ops_manager"]}><LeadReview /></Protected>} />
            <Route path="/vendor" element={<Protected allow={["vendor"]}><VendorPortal /></Protected>} />
            <Route path="/telephony" element={<Protected allow={["admin", "ops_manager", "agent", "bpo_closer"]}><Telephony /></Protected>} />
            <Route path="/sophia" element={<Protected allow={["admin", "ops_manager", "agent", "bpo_closer"]}><SophiaOps /></Protected>} />
            <Route path="/appointments" element={<Protected allow={["admin", "ops_manager", "agent"]}><Appointments /></Protected>} />
            <Route path="/quotes" element={<Protected><Quotes /></Protected>} />
            <Route path="/requote" element={<Protected><Requote /></Protected>} />
            <Route path="/policies" element={<Protected><Policies /></Protected>} />
            <Route path="/followups" element={<Protected allow={["admin", "ops_manager", "agent"]}><FollowUps /></Protected>} />
            <Route path="/commissions" element={<Protected allow={["admin", "ops_manager", "agent"]}><Commissions /></Protected>} />
            <Route path="/scorecard" element={<Protected allow={["admin", "ops_manager", "agent"]}><Scorecard /></Protected>} />
            <Route path="/team" element={<Protected><Team /></Protected>} />
            <Route path="/agency-admin" element={<Protected allow={["admin", "ops_manager"]}><AgencyAdmin /></Protected>} />
            <Route path="/growth-command" element={<Protected allow={["platform_owner"]}><GrowthCommandCenter /></Protected>} />
            <Route path="/campaign-launch" element={<Protected allow={["platform_owner"]}><CampaignLaunchpad /></Protected>} />
            <Route path="/content-studio" element={<Protected allow={["platform_owner"]}><ContentStudio /></Protected>} />
            <Route path="/marketing-operations" element={<Protected allow={["platform_owner"]}><MarketingOperations /></Protected>} />
            <Route path="/platform" element={<Protected allow={["platform_owner"]}><Platform /></Protected>} />
            <Route path="/campaign-director" element={<Protected allow={["platform_owner"]}><CampaignDirector /></Protected>} />
            <Route path="/lead-marketplace" element={<Protected allow={["platform_owner"]}><LeadMarketplace /></Protected>} />
            <Route path="/lead-buyers" element={<Protected allow={["platform_owner"]}><BuyerMarketplace /></Protected>} />
            <Route path="/growth" element={<Protected allow={["admin", "ops_manager", "agent"]}><GrowthHub /></Protected>} />
            <Route path="/exclusive-agency-leads" element={<Protected allow={["admin", "ops_manager"]}><ExclusiveAgencyLeads /></Protected>} />
            <Route path="/import" element={<Protected allow={["admin", "agent", "bpo_closer"]}><Import /></Protected>} />
            <Route path="/recovery" element={<Protected allow={["admin", "ops_manager", "agent"]}><BookRecovery /></Protected>} />
            <Route path="/vault" element={<Protected allow={["admin", "ops_manager", "agent"]}><CredentialVault /></Protected>} />
            <Route path="/credential-review" element={<Protected allow={["admin", "ops_manager"]}><CredentialMigrationReview /></Protected>} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </GuidedTourProvider>
          </CopilotProvider>
          </RestrictedVaultProvider>
          </UniversalIntakeProvider>
          </ReferenceProvider>
          </PresentationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
